import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Home, TrendingUp, Users, FileText, CheckCircle, ArrowRight, 
  MapPin, Phone, Building2, Scale, Landmark, Award,
  ChevronRight, Star, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import CalendlyButton from '../components/CalendlyButton';

const DivisionImmobiliere = () => {
  const benefits = [
    {
      icon: TrendingUp,
      title: "+20 à 40% de Plus-Value",
      description: "La somme des lots divisés vaut toujours plus que le bien entier."
    },
    {
      icon: Home,
      title: "Revenus Locatifs",
      description: "Divisez et louez une partie pour des revenus complémentaires."
    },
    {
      icon: Users,
      title: "Succession Simplifiée",
      description: "Chaque héritier reçoit son lot, fini les conflits d'indivision."
    },
    {
      icon: Scale,
      title: "Sortie d'Indivision",
      description: "Divisez pour que chacun devienne propriétaire de son lot."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Étude de Faisabilité",
      description: "Analyse gratuite de votre bien : PLU, accès, réseaux, potentiel."
    },
    {
      number: "02",
      title: "Autorisations",
      description: "Déclaration préalable, permis d'aménager... on gère tout."
    },
    {
      number: "03",
      title: "Coordination Experts",
      description: "Géomètre, notaire, architecte : un seul interlocuteur."
    },
    {
      number: "04",
      title: "Réalisation",
      description: "Division cadastrale, actes notariés, vous êtes propriétaire."
    }
  ];

  const faqs = [
    {
      question: "Faut-il une autorisation pour diviser un bien immobilier ?",
      answer: "Oui, selon le type de division : déclaration préalable, permis d'aménager ou permis de construire.",
      link: "/blog/faut-il-une-autorisation-pour-la-division-immobiliere-guide-complet-2025"
    },
    {
      question: "Combien coûte une division immobilière ?",
      answer: "Entre 3 000€ et 15 000€ selon la complexité : géomètre, notaire, raccordements.",
      link: "/blog/les-3-points-essentiels-pour-la-faisabilite-dune-division-immobiliere"
    },
    {
      question: "Qui contacter pour diviser sa maison ?",
      answer: "Géomètre-expert, notaire, et G Division pour coordonner l'ensemble.",
      link: "/blog/qui-appeler-pour-diviser-sa-maison-le-guide-des-professionnels"
    },
    {
      question: "Peut-on diviser un bien en succession ?",
      answer: "Oui, c'est même très avantageux fiscalement et évite les conflits d'indivision.",
      link: "/blog/diviser-un-bien-en-succession-les-avantages-fiscaux-et-patrimoniaux-enormes"
    }
  ];

  const articles = [
    {
      title: "Guide Ultime 2025",
      slug: "division-immobiliere-en-2025-le-guide-ultime-pour-maximiser-votre-patrimoine",
      tag: "COMPLET"
    },
    {
      title: "Autorisations Nécessaires",
      slug: "faut-il-une-autorisation-pour-la-division-immobiliere-guide-complet-2025",
      tag: "JURIDIQUE"
    },
    {
      title: "3 Points de Faisabilité",
      slug: "les-3-points-essentiels-pour-la-faisabilite-dune-division-immobiliere",
      tag: "TECHNIQUE"
    },
    {
      title: "Avantages en Succession",
      slug: "diviser-un-bien-en-succession-les-avantages-fiscaux-et-patrimoniaux-enormes",
      tag: "FISCAL"
    },
    {
      title: "Qui Appeler ?",
      slug: "qui-appeler-pour-diviser-sa-maison-le-guide-des-professionnels",
      tag: "PRATIQUE"
    },
    {
      title: "Sortir d'Indivision",
      slug: "sortir-dune-indivision-les-solutions",
      tag: "INDIVISION"
    }
  ];

  // Schema.org structured data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Division Immobilière",
    "provider": {
      "@type": "Organization",
      "name": "G Division",
      "url": "https://divisionimmobiliere.com"
    },
    "description": "Expert en division immobilière : division parcellaire, division de bâtiment, sortie d'indivision. Accompagnement complet de votre projet.",
    "areaServed": "France",
    "serviceType": "Conseil en division immobilière"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Division Immobilière : Expert en Valorisation de Patrimoine | G Division</title>
        <meta name="description" content="Division immobilière : divisez votre terrain ou maison pour multiplier sa valeur jusqu'à +40%. Étude de faisabilité gratuite. Expert en division parcellaire, sortie d'indivision, succession." />
        <meta name="keywords" content="division immobilière, diviser terrain, diviser maison, division parcellaire, sortie indivision, division succession, valorisation patrimoine, G Division" />
        <link rel="canonical" href="https://divisionimmobiliere.com/division-immobiliere" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Division Immobilière : Multipliez la Valeur de Votre Bien | G Division" />
        <meta property="og:description" content="Divisez votre terrain ou maison pour une plus-value de +20 à 40%. Étude de faisabilité gratuite avec G Division, expert en division immobilière." />
        <meta property="og:url" content="https://divisionimmobiliere.com/division-immobiliere" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Division Immobilière : Expert G Division" />
        <meta name="twitter:description" content="Divisez votre bien immobilier et multipliez sa valeur. Étude gratuite." />
      </Helmet>

      {/* Schema.org JSON-LD */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="pt-20" data-testid="division-immobiliere-page">
        
        {/* HERO — Sexy Architectural Theme */}
        <section className="relative bg-[#0a1525] py-20 md:py-32 overflow-hidden">
          {/* Architectural blueprint pattern overlay */}
          <svg
            className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
          >
            <defs>
              <pattern id="blueprintGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#c9a227" strokeWidth="0.5" />
              </pattern>
              <pattern id="blueprintGridLg" width="200" height="200" patternUnits="userSpaceOnUse">
                <path d="M 200 0 L 0 0 0 200" fill="none" stroke="#c9a227" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#blueprintGrid)" />
            <rect width="100%" height="100%" fill="url(#blueprintGridLg)" />
          </svg>

          {/* Architectural plan SVG — abstract floor plan lines on the right */}
          <svg
            className="absolute right-0 top-1/2 -translate-y-1/2 w-[700px] h-[700px] opacity-[0.12] pointer-events-none hidden lg:block"
            viewBox="0 0 600 600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            stroke="#c9a227"
            strokeWidth="2"
            aria-hidden="true"
          >
            {/* Outer building outline */}
            <rect x="80" y="80" width="440" height="440" />
            {/* Inner walls forming rooms */}
            <line x1="80" y1="280" x2="320" y2="280" />
            <line x1="320" y1="80" x2="320" y2="520" />
            <line x1="320" y1="380" x2="520" y2="380" />
            <line x1="200" y1="280" x2="200" y2="520" />
            {/* Doors (arcs) */}
            <path d="M 200 280 A 40 40 0 0 1 240 320" strokeWidth="1.5" />
            <path d="M 320 280 A 40 40 0 0 0 360 320" strokeWidth="1.5" />
            {/* Dimension marks */}
            <line x1="80" y1="40" x2="520" y2="40" strokeWidth="1" />
            <line x1="80" y1="35" x2="80" y2="45" strokeWidth="1" />
            <line x1="520" y1="35" x2="520" y2="45" strokeWidth="1" />
            <line x1="40" y1="80" x2="40" y2="520" strokeWidth="1" />
            <line x1="35" y1="80" x2="45" y2="80" strokeWidth="1" />
            <line x1="35" y1="520" x2="45" y2="520" strokeWidth="1" />
            {/* Compass rose */}
            <circle cx="460" cy="140" r="30" strokeWidth="1.5" />
            <line x1="460" y1="115" x2="460" y2="165" strokeWidth="1" />
            <line x1="435" y1="140" x2="485" y2="140" strokeWidth="1" />
          </svg>

          {/* Glow orbs */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#c9a227]/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500/15 rounded-full blur-3xl pointer-events-none"></div>

          {/* Bottom blueprint accent line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a227]/40 to-transparent pointer-events-none"></div>

          <div className="relative max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-4xl">
              {/* Architectural badge */}
              <div className="inline-flex items-center gap-3 mb-8">
                <div className="flex items-center gap-2 bg-[#c9a227]/10 backdrop-blur-md border border-[#c9a227]/30 text-[#c9a227] text-xs font-bold px-4 py-2 rounded-full uppercase tracking-[0.2em]">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#c9a227] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#c9a227]"></span>
                  </span>
                  Expertise · Division Immobilière
                </div>
              </div>
              
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.05]">
                Division Immobilière
                <span className="block bg-gradient-to-r from-[#c9a227] via-[#e6c14d] to-[#c9a227] bg-clip-text text-transparent">
                  Le savoir-faire d'un expert
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-10 max-w-2xl">
                Diviser un terrain ou une maison ne s'improvise pas. <strong className="text-white">Réglementation, faisabilité, urbanisme, mise en copropriété, fiscalité</strong>&nbsp;: chaque étape exige un professionnel. Nous maîtrisons la chaîne complète.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="group bg-[#c9a227] hover:bg-[#b08d22] text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg shadow-[#c9a227]/25 hover:shadow-[0_0_30px_rgba(201,162,39,0.5)] transition-all duration-300">
                    Étude de Faisabilité Gratuite
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                  </Button>
                </Link>
                <CalendlyButton 
                  url="https://calendly.com/gdivision/rdv"
                  text="Prendre RDV"
                  className="border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/60 backdrop-blur-sm px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-300"
                />
              </div>
              
              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-6 mt-10 pt-10 border-t border-white/10">
                <div className="flex items-center gap-2 text-gray-400">
                  <CheckCircle className="text-green-400" size={20} />
                  <span>Consultation gratuite</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <CheckCircle className="text-green-400" size={20} />
                  <span>Partout en France</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <CheckCircle className="text-green-400" size={20} />
                  <span>Accompagnement de A à Z</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* DEFINITION */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-[#c9a227] uppercase tracking-[0.2em] text-xs font-semibold">
                  Comprendre
                </span>
                <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mt-3 mb-6">
                  Qu'est-ce que la Division Immobilière ?
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  La <strong>division immobilière</strong> consiste à fractionner un bien unique 
                  (terrain ou bâtiment) en plusieurs lots distincts, chacun pouvant être vendu, 
                  loué ou transmis séparément.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-blue-600" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1e3a5f]">Division Parcellaire</h3>
                      <p className="text-gray-600 text-sm">Diviser un terrain en plusieurs lots à bâtir</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="text-emerald-600" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1e3a5f]">Division de Bâtiment</h3>
                      <p className="text-gray-600 text-sm">Transformer une maison en plusieurs appartements</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="text-purple-600" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1e3a5f]">Sortie d'Indivision</h3>
                      <p className="text-gray-600 text-sm">Partager un bien entre plusieurs propriétaires</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-[#1e3a5f] to-[#0f1d2f] rounded-3xl p-8 text-white border border-[#c9a227]/20 shadow-2xl shadow-[#c9a227]/10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#c9a227]/20 rounded-lg flex items-center justify-center">
                      <ShieldCheck className="text-[#c9a227]" size={20} />
                    </div>
                    <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#c9a227]">
                      Une Expertise Complète
                    </h3>
                  </div>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    Diviser un bien n'est pas qu'une question de m². C'est un métier qui mobilise des compétences pointues en urbanisme, droit immobilier, fiscalité et construction.
                  </p>

                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-[#c9a227] flex-shrink-0 mt-0.5" size={18} />
                      <div>
                        <p className="font-semibold text-white text-sm">Faisabilité &amp; urbanisme</p>
                        <p className="text-gray-400 text-xs">PLU, COS, servitudes, accès, ABF</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-[#c9a227] flex-shrink-0 mt-0.5" size={18} />
                      <div>
                        <p className="font-semibold text-white text-sm">Cadre juridique</p>
                        <p className="text-gray-400 text-xs">Mise en copropriété, géomètre, division parcellaire</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-[#c9a227] flex-shrink-0 mt-0.5" size={18} />
                      <div>
                        <p className="font-semibold text-white text-sm">Travaux &amp; conformité</p>
                        <p className="text-gray-400 text-xs">Maître d'œuvre, mise aux normes, DTA</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-[#c9a227] flex-shrink-0 mt-0.5" size={18} />
                      <div>
                        <p className="font-semibold text-white text-sm">Fiscalité optimisée</p>
                        <p className="text-gray-400 text-xs">TVA marchand de biens, plus-values, régime appliqué</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-[#c9a227] flex-shrink-0 mt-0.5" size={18} />
                      <div>
                        <p className="font-semibold text-white text-sm">Commercialisation</p>
                        <p className="text-gray-400 text-xs">Mise sur le marché des lots aux meilleurs prix</p>
                      </div>
                    </li>
                  </ul>

                  <div className="mt-6 pt-6 border-t border-white/10">
                    <p className="text-xs text-gray-400 italic">
                      Notre équipe maîtrise chaque maillon. <span className="text-white">Vous traitez avec un seul interlocuteur</span>, du premier rendez-vous à la signature finale.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <span className="text-[#c9a227] uppercase tracking-[0.2em] text-xs font-semibold">
                Avantages
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mt-3">
                Pourquoi Diviser Votre Bien ?
              </h2>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] rounded-xl flex items-center justify-center mb-4">
                    <benefit.icon className="text-[#c9a227]" size={28} />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1e3a5f] mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <span className="text-[#c9a227] uppercase tracking-[0.2em] text-xs font-semibold">
                Processus
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mt-3">
                Comment Ça Marche ?
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="text-6xl font-bold text-gray-100 absolute -top-4 left-0">
                    {step.number}
                  </div>
                  <div className="relative pt-8">
                    <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1e3a5f] mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">
                      {step.description}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <ChevronRight className="hidden lg:block absolute top-1/2 -right-4 text-gray-300" size={24} />
                  )}
                </div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <Link to="/contact">
                <Button className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white px-8 py-6 text-lg font-semibold rounded-xl">
                  Démarrer Mon Projet
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <span className="text-[#c9a227] uppercase tracking-[0.2em] text-xs font-semibold">
                Questions Fréquentes
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mt-3">
                Tout Savoir sur la Division Immobilière
              </h2>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-[#1e3a5f] text-lg mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {faq.answer}
                  </p>
                  <Link 
                    to={faq.link}
                    className="inline-flex items-center text-[#c9a227] hover:text-[#1e3a5f] font-medium text-sm transition-colors"
                  >
                    Lire l'article complet
                    <ArrowRight size={16} className="ml-1" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ARTICLES */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <span className="text-[#c9a227] uppercase tracking-[0.2em] text-xs font-semibold">
                Ressources
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1e3a5f] mt-3">
                Nos Guides sur la Division Immobilière
              </h2>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article, index) => (
                <Link 
                  key={index}
                  to={`/blog/${article.slug}`}
                  className="group bg-gray-50 hover:bg-[#1e3a5f] rounded-xl p-6 transition-all duration-300"
                >
                  <span className="inline-block bg-[#c9a227]/20 text-[#c9a227] text-xs font-bold px-3 py-1 rounded-full mb-4 group-hover:bg-[#c9a227]/30">
                    {article.tag}
                  </span>
                  <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#1e3a5f] group-hover:text-white transition-colors">
                    {article.title}
                  </h3>
                  <div className="flex items-center text-[#c9a227] mt-4 text-sm font-medium">
                    Lire l'article
                    <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link to="/blog">
                <Button variant="outline" className="border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white px-8 py-4 font-semibold rounded-xl">
                  Voir Tous les Articles
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-20 md:py-28 bg-gradient-to-br from-[#1e3a5f] via-[#1e3a5f] to-[#0f1d2f] relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#c9a227]/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 text-[#c9a227] text-sm font-semibold px-4 py-2 rounded-full mb-6">
              <Star size={16} />
              Étude de faisabilité offerte
            </div>
            
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Prêt à Valoriser Votre Patrimoine ?
            </h2>
            
            <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
              Découvrez le potentiel de votre bien avec une étude de faisabilité gratuite. 
              Nos experts analysent votre projet en 48h.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-[#c9a227] hover:bg-[#b08d22] text-white px-10 py-6 text-lg font-bold rounded-xl shadow-lg">
                  Demander Mon Étude Gratuite
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="https://wa.me/33645040975" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-6 text-lg font-semibold rounded-xl">
                  <Phone className="mr-2" size={20} />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
        
      </div>
    </>
  );
};

export default DivisionImmobiliere;
